import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import { RelativeComponentLoader } from "@/components/loader";
import { SnowflakeQueryIcon } from "@/assets/icons";
import { getWarehouseDecisionData, getWarehouseQueryExecutionTimes } from "@/helpers/apis";
import {
  DecisionGraph,
  ExecutionTimeGraph,
  BytesScannedGraph,
  OverloadTimeGraph,
  QueryCountGraph,
} from "./graphs";
import { PERIOD_OPTIONS } from "../../constants";
import { NativeSelect, Stack, Tabs, TabsList, TabsTrigger } from "@altimateai/lego";

const GET_WAREHOUSE_QUERY_EXECUTION_TIME = "GET_WAREHOUSE_QUERY_EXECUTION_TIME";
const GET_WAREHOUSE_DECISION_DATA = "GET_WAREHOUSE_DECISION_DATA";

interface ChartTileProps {
  warehouse_rk: string;
  graphType: string;
  setGraphType: (value: string) => void;
}

const ChartTile = ({ warehouse_rk, graphType, setGraphType }: ChartTileProps) => {
  const { instance_id } = useParams();
  const [period, setPeriod] = useState({
    decision: 7,
    execution_time: 7,
  });
  const [startDate, setStartDate] = useState(dayjs().subtract(1, "week"));
  const [endDate, setEndDate] = useState(dayjs());

  const {
    data: query_execution_time_graph_data = { query_execution_time: [] },
    isLoading: isLoadingQueryExecutionTimeData,
  } = useQuery({
    queryKey: [GET_WAREHOUSE_QUERY_EXECUTION_TIME, warehouse_rk, period?.execution_time],
    queryFn: () =>
      getWarehouseQueryExecutionTimes(instance_id || "", warehouse_rk, {
        period: period?.execution_time,
      }),
    enabled:
      graphType === "execution_time" ||
      graphType === "bytes_scanned" ||
      graphType === "overload_time" ||
      graphType === "query_count",
  });

  const { data: decision_graph_data, isLoading: isLoadingDecisionData } = useQuery({
    queryKey: [GET_WAREHOUSE_DECISION_DATA, warehouse_rk, period?.decision],
    queryFn: () =>
      getWarehouseDecisionData(instance_id || "", warehouse_rk, { period: period?.decision }),
    enabled: graphType === "decision",
  });

  // The useEffect below does two things:
  // 1. It checks if the graph type is decision and if the decision graph data is available
  //    If the decision graph data is available, it sets the start and end date based on the data
  // 2. It checks if the graph type is execution time and if the query execution time graph data is available
  //    If the query execution time graph data is available, it sets the start and end date based on the data
  useEffect(() => {
    if (
      graphType === "decision" &&
      decision_graph_data?.decision_chart &&
      decision_graph_data?.decision_chart.length > 0
    ) {
      if (decision_graph_data?.decision_chart.length > 0) {
        const dates = decision_graph_data?.decision_chart.map(
          (item: { date: string }) => item.date
        );
        // This change is required because of global locale dayjs configuration
        // The date format is not consistent in the data so we need to remove the dot
        const parsedDates = dates.map((date: string) =>
          dayjs(date.replace(".", ""), "DD MMM YYYY")
        );
        parsedDates.sort((a: dayjs.Dayjs, b: dayjs.Dayjs) => a.unix() - b.unix());
        const minDate = parsedDates[0];
        const maxDate = parsedDates[parsedDates.length - 1];
        setStartDate(minDate);
        setEndDate(maxDate);
      }
      return;
    }
    if (
      (graphType === "execution_time" || graphType === "bytes_scanned") &&
      query_execution_time_graph_data?.query_execution_time &&
      query_execution_time_graph_data?.query_execution_time.length > 0
    ) {
      if (query_execution_time_graph_data?.query_execution_time.length > 0) {
        const dates = query_execution_time_graph_data?.query_execution_time.map(
          (item: { date: string }) => item.date
        );
        // This change is required because of global locale dayjs configuration
        // The date format is not consistent in the data so we need to remove the dot
        const parsedDates = dates.map((date: string) =>
          dayjs(date.replace(".", ""), "DD MMM YYYY")
        );
        parsedDates.sort((a: dayjs.Dayjs, b: dayjs.Dayjs) => a.unix() - b.unix());
        const minDate = parsedDates[0];
        const maxDate = parsedDates[parsedDates.length - 1];
        setStartDate(minDate);
        setEndDate(maxDate);
      }
    }
  }, [
    decision_graph_data,
    graphType,
    period?.decision,
    period?.execution_time,
    query_execution_time_graph_data,
  ]);

  const currentPeriod = graphType === "decision" ? period?.decision : period?.execution_time;
  const yearMode = PERIOD_OPTIONS.find(x => x.value === currentPeriod)?.label === "Year";

  const dateFormat = yearMode ? "MMM 'YY" : "DD MMM 'YY";

  return (
    <div>
      <Stack className="mb-2" gap={"sm"} align={"center"}>
        <Tabs value={graphType} onValueChange={setGraphType}>
          <TabsList>
            {[
              { label: "Agent Decisions", value: "decision" },
              {
                label: (
                  <Stack align={"center"} gap={"xs"}>
                    <SnowflakeQueryIcon />
                    Query Execution Time
                  </Stack>
                ),
                value: "execution_time",
              },
              {
                label: (
                  <Stack align={"center"} gap={"xs"}>
                    <SnowflakeQueryIcon />
                    Total Bytes Scanned
                  </Stack>
                ),
                value: "bytes_scanned",
              },
              {
                label: (
                  <Stack align={"center"} gap={"xs"}>
                    <SnowflakeQueryIcon />
                    Query Queuing Time
                  </Stack>
                ),
                value: "overload_time",
              },
              {
                label: (
                  <Stack align={"center"} gap={"xs"}>
                    <SnowflakeQueryIcon />
                    Query Count
                  </Stack>
                ),
                value: "query_count",
              },
            ].map(({ label, value }) => (
              <TabsTrigger key={value} value={value}>
                {label}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
        <div className="spacer" />
        <div className="text-muted">
          {startDate.format(dateFormat)}
          {" - "}
          {endDate.format(dateFormat)}
        </div>

        <NativeSelect
          id="details-graph-period"
          options={PERIOD_OPTIONS.map(o => ({ ...o, value: String(o.value) }))}
          onChange={(value: string) => {
            const numValue = Number(value);
            if (graphType === "decision") {
              setPeriod({ ...period, decision: numValue });
            } else {
              setPeriod({ ...period, execution_time: numValue });
            }
          }}
          value={String(currentPeriod)}
          size={"sm"}
          label={PERIOD_OPTIONS.find(x => x.value === currentPeriod)?.label}
        />
      </Stack>
      {graphType === "decision" &&
        (isLoadingDecisionData ? (
          <RelativeComponentLoader label="Loading..." componentHeight={40} />
        ) : (
          <DecisionGraph data={decision_graph_data?.decision_chart || []} />
        ))}
      {graphType === "execution_time" &&
        (isLoadingQueryExecutionTimeData ? (
          <RelativeComponentLoader label="Loading..." componentHeight={40} />
        ) : (
          <ExecutionTimeGraph data={query_execution_time_graph_data.query_execution_time} />
        ))}
      {graphType === "bytes_scanned" &&
        (isLoadingQueryExecutionTimeData ? (
          <RelativeComponentLoader label="Loading..." componentHeight={40} />
        ) : (
          <BytesScannedGraph data={query_execution_time_graph_data.query_execution_time} />
        ))}
      {graphType === "overload_time" &&
        (isLoadingQueryExecutionTimeData ? (
          <RelativeComponentLoader label="Loading..." componentHeight={40} />
        ) : (
          <OverloadTimeGraph data={query_execution_time_graph_data.query_execution_time} />
        ))}
      {graphType === "query_count" &&
        (isLoadingQueryExecutionTimeData ? (
          <RelativeComponentLoader label="Loading..." componentHeight={40} />
        ) : (
          <QueryCountGraph data={query_execution_time_graph_data.query_execution_time} />
        ))}
    </div>
  );
};

export default ChartTile;
